import { useHealthInsuranceContext } from 'contexts/HealthInsuranceContext';
import { WizardSlot } from 'layout/wizard/slots';
import { Form, formModelGet, Icon, useQuestionsContext, UserCircleIcon } from '@portal/ui-library';
import { useApplicationHelpers } from 'hooks/useApplicationHelpers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import cloneDeep from 'lodash/cloneDeep';
import groupBy from 'lodash/groupBy';
import { partnerField } from 'models';
import { useTranslation, Trans } from 'react-i18next';
import { downloadData, downloadDisclaimerDocument } from 'utils/fetchDocument';

import Typography from '@mui/material/Typography';
import axios from 'axios';
import classNames from 'classnames';
import { DownloadOfferDialog } from 'components/DownloadButton/DownloadOfferDialog';
import { useApplicationCalculate } from 'hooks/useApplicationCalculate';
import { useTariffQuery } from 'hooks/useTariffQuery';
import {
  ApplicationService,
  Partner,
  PersonRoleEnum,
  Question,
  TariffSectionEnum
} from 'models/extension-generated';
import { InsuredPersonDetail } from 'pages/DigitalOrderPage/components/InsuredPersonDetail/InsuredPersonDetail';
import { NavigationSection } from 'pages/DigitalOrderPage/components/NavigationSection/NavigationSection';
import {
  Cells,
  OfferTariffTable,
  TariffRow
} from 'pages/DigitalOrderPage/components/OfferTariffTable';
import { PaymentContributorDetail } from 'pages/DigitalOrderPage/components/PaymentContributorDetial/PaymentContributorDetail';
import { PolicyHolderDetail } from 'pages/DigitalOrderPage/components/PolicyHolderDetail/PolicyHolderDetail';
import { EVENT_IDS } from 'pages/DigitalOrderPage/constants';
import {
  HealthQuestionsProps,
  InsuredPersonDetailProps
} from 'pages/DigitalOrderPage/models/insuredPersonDetailModels';
import { useEffect, useState } from 'react';

import { formatGermanDate } from 'utils/dates';
import {
  formatAddress,
  formatEnum,
  formatName,
  formatPercent,
  optionalValue
} from 'utils/formatters';
import { getQuestionsByCriteria } from 'utils/getQuestionsByCriteria';
import { formatPriceAsText } from 'utils/string';
import { AccompaniedDocumentsDownload } from './components/AccompaniedDocumentsDownload';
import { DisclaimerCheckbox } from './components/DisclaimerCheckbox';
import { MarketingConsentForm } from './components/MarketingConsentForm';
import styles from './index.module.scss';
import { mapHealthQuestion } from './utils/mapHealthQuestion';
import { useConfigContext } from 'contexts/ConfigContext';
import { findCountryField } from 'utils/countries';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';

const fetchPartnerQuestions = (insuranceStart: string) => async (partner: Partner) => {
  const tariffs = partner.applicationInformation?.tariffInformation?.selectedTariffs || [];
  const birthDate = partner.birthDate;
  const questions = await getQuestionsByCriteria({ tariffs, insuranceStart, birthDate });

  if (!partner.id)
    return {
      partnerId: '',
      questions: []
    };

  return {
    partnerId: partner.id,
    questions
  };
};

const usePartnersQuestions = (
  partners: Array<Partner>,
  insuranceStart: string
): {
  isLoading: boolean;
  partnersQuestions: {
    [key: string]: Array<Question>;
  };
} => {
  const [isLoading, setIsLoading] = useState(false);
  const [partnersQuestions, setPartnersQuestions] = useState<{
    [key: string]: Array<Question>;
  }>({});

  useEffect(() => {
    setIsLoading(true);

    const fetchQuestionsForPartners = async (partners: Array<Partner>) => {
      try {
        const partnersWithQuestions: Array<{ partnerId: string; questions: Array<Question> }> =
          await Promise.all(partners.map(fetchPartnerQuestions(insuranceStart)));

        const obj = partnersWithQuestions.reduce((acc, { partnerId, questions }) => {
          return {
            ...acc,
            [partnerId]: questions
          };
        }, {});
        setPartnersQuestions(obj);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuestionsForPartners(partners);
  }, []);

  return {
    isLoading,
    partnersQuestions: partnersQuestions
  };
};

export const SummaryStep = () => {
  const { t } = useTranslation('summaryPage');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { state, reducer, dispatch } = useHealthInsuranceContext();
  const { getSinglePartnerByRole, getPartnersByRole } = useApplicationHelpers(state.model);
  const { config } = useConfigContext();
  const { questions } = useQuestionsContext();
  const { data } = useApplicationCalculate(state.model, questions, true);
  const insuredPartners = getPartnersByRole(PersonRoleEnum.INSURED_PERSON);
  const insuranceStart = state.model.applicationStart || '';

  const { partnersQuestions } = usePartnersQuestions(insuredPartners, insuranceStart);

  const insuredPersons: Array<Partner> = getPartnersByRole(PersonRoleEnum.INSURED_PERSON)
    .sort((a, b) => (a.order! > b.order! ? 1 : -1))
    .map((p, i) => ({ ...p, order: 1 * (i + 1) }));
  const policyHolder = getSinglePartnerByRole(PersonRoleEnum.POLICY_HOLDER);
  const paymentContributor = getSinglePartnerByRole(PersonRoleEnum.PAYMENT_CONTRIBUTOR);

  const paymentContributorResidence = paymentContributor.permanentResidence;
  const paymentContributorAccount = paymentContributor.bankDetails;

  const formatMarketingStreams = (partner: Partner): string => {
    const streams = [];
    if (partner.applicationInformation?.acceptsPromotionalPurposesEmail) {
      streams.push(t('utils.email'));
    }
    if (partner.applicationInformation?.acceptsPromotionalPurposesPhone) {
      streams.push(t('utils.phone'));
    }

    return streams.length > 0 ? streams.join(', ') : '-';
  };

  const { id: holderId = '' } = getSinglePartnerByRole(PersonRoleEnum.POLICY_HOLDER);

  const hasConsentPath = partnerField(holderId, 'applicationInformation.acceptsDataProtection');
  const hasDownloadedContractInformationDocumentPath = partnerField(
    holderId,
    'applicationInformation.hasDownloadedContractInformationDocument'
  );
  const hasDownloadedAccessibleDocumentsPath = partnerField(
    holderId,
    'applicationInformation.hasDownloadedAccessibleDocuments'
  );
  const hasConsent = formModelGet(state.model, hasConsentPath);
  const downloadFile = async (): Promise<void> => {
    try {
      const cloned = { ...cloneDeep(state.model) };
      const applicationId: string = await (async () => {
        if (cloned.id) {
          return cloned.id;
        }
        const { id } = await ApplicationService.createPublicApplication({ requestBody: cloned });
        reducer.updateValue({
          key: 'id',
          value: id
        });
        return id;
      })();

      const response = await axios.post(
        `/api/public/new/application/accompanyDocs`,
        {
          ...cloned,
          id: applicationId
        },
        {
          responseType: 'blob',
          params: {
            accessible: false
          }
        }
      );
      const responseAccessible = await axios.post(
        `/api/public/new/application/accompanyDocs`,
        {
          ...cloned,
          id: applicationId
        },
        {
          responseType: 'blob',
          params: {
            accessible: true
          }
        }
      );

      downloadData(response.data, `Vertragsinformationen.pdf`);
      downloadData(responseAccessible.data, `Vertragsinformationen-barrierefrei.pdf`);
      reducer.updateValue({
        key: hasDownloadedContractInformationDocumentPath,
        value: true
      });
      reducer.updateValue({
        key: hasDownloadedAccessibleDocumentsPath,
        value: true
      });
      setIsDialogOpen(false);
    } catch (error) {
      throw new Error(`Error occurred during download: ${error}`);
    }
  };

  const handleConsentClick = (hasConsent: boolean) => {
    reducer.updateValue({
      key: hasConsentPath,
      value: hasConsent
    });
  };

  const sendMail = () => {
    window.location.href = 'mailto:vertriebsservice@lkh.de';
  };

  const { tariffs } = useTariffQuery();

  const renderInsuredPersons = () =>
    insuredPersons.map((partner) => {
      if (!partner.id) return <></>;

      const selectedTariffs =
        partner.applicationInformation?.tariffInformation?.selectedTariffs || [];
      const partnerQuestions = partnersQuestions[partner.id] || [];
      const groupedQuestions = groupBy(partnerQuestions, 'sections');

      const stationaryHealthQuestions: HealthQuestionsProps = {
        questions: groupedQuestions[TariffSectionEnum.STATIONAER]?.map(mapHealthQuestion(partner))
      };
      const teethHealthQuestions: HealthQuestionsProps = {
        questions: groupedQuestions[TariffSectionEnum.ZAHN]?.map(mapHealthQuestion(partner))
      };

      const mappedPartner: InsuredPersonDetailProps = {
        order: partner.order || 0,
        tariffTable: (
          <>
            <OfferTariffTable
              allowEditing={false}
              partnerId={partner.id}
              summaryRow={{
                id: 'sampleId',
                product: (
                  <Typography component="div" variant="bodyMDBold" className="text-white-100 p-s">
                    {t('monthlyPayment')}
                  </Typography>
                ),
                price: (
                  <Cells.Price
                    id={`${partner.id}-price`}
                    text={formatPriceAsText(
                      data?.partners.find(({ partnerId }) => partnerId === partner.id)
                        ?.calculationMonthly?.totalAmount
                    )}
                    className="text-white-100 font-bold"
                  />
                )
              }}
              tariffs={selectedTariffs.map((tariff) => {
                const { section, id } = tariff;

                const description = tariffs.find((t) => {
                  return t.id === id;
                })?.description;

                const tariffPrice = data?.partners
                  .find(({ partnerId }) => partnerId === partner.id)
                  ?.tariffCalculations?.find((t) => {
                    return t.tariffId === id;
                  })?.calculationMonthly;

                const tariffRow: TariffRow = {
                  id: id,
                  product: (
                    <Cells.ProductLabel
                      label={t(`TariffSectionEnum.${section}`, {
                        ns: 'contractsEnums'
                      })}
                    />
                  ),
                  label: <Cells.TariffLabel label={id} tooltip={description || ''} />,
                  price: (
                    <Cells.Price
                      text={formatPriceAsText(tariffPrice?.amount)}
                      className="text-text-100"
                    />
                  )
                };
                return tariffRow;
              })}
            />
          </>
        ),
        stationaryHealthQuestions,
        teethHealthQuestions,
        firstName: partner.firstname,
        isPolicyHolder: policyHolder.id === partner.id,
        // [Personal data]
        name: optionalValue(partner, formatName) as string,
        lastName: partner.lastname,
        birthDate: partner.birthDate,
        address: formatAddress(partner.permanentResidence),
        salutation: formatEnum(partner?.salutation, 'SalutationEnum', 'contractsEnums'),
        academicTitle: formatEnum(partner.title, 'TitleEnum', 'contractsEnums'),
        maritalStatus: formatEnum(partner.maritalStatus, 'MaritalStatusEnum', 'contractsEnums'),
        // nationality: ??? [NOT IN DTO]
        // [Employment Data]
        professionalPosition: formatEnum(
          partner.applicationInformation?.employmentGroup,
          'EmploymentGroupEnum',
          'contractsEnums'
        ),
        currentOccupation: formatEnum(
          partner?.applicationInformation?.profession,
          'ProfessionEnum',
          'contractsEnums'
        ),
        employer: partner.applicationInformation?.employer,
        trainingStart: partner.applicationInformation?.trainingStart,
        trainingEnd: partner.applicationInformation?.trainingEnd,
        isLivingInGermany: partner.livingInGermany,
        street: partner.permanentResidence?.street,
        houseNumber: partner.permanentResidence?.houseNumber,
        postalCode: partner.permanentResidence?.postalCode,
        city: partner.permanentResidence?.city,
        country: findCountryField(
          config.countries || [],
          'code',
          partner.permanentResidence?.country,
          'name'
        ),
        hasForeignResidence: partner.hasLivedInForeignCountry,
        foreignCountry: partner.foreignCountry,
        taxNumber: partner?.taxNumber,
        needSituation: formatEnum(
          partner.applicationInformation?.tariffInformation?.needSituation,
          'NeedSituationEnum',
          'contractsEnums'
        ),
        insuranceStart: state.model.applicationStart,
        federalState: formatEnum(
          partner.applicationInformation?.federalState,
          'FederalStateEnum',
          'contractsEnums'
        ),
        aidClaim: optionalValue(
          partner?.applicationInformation?.tariffInformation?.claimAmount,
          formatPercent
        ) as string,
        hadInsuranceSince2012: partner?.applicationInformation?.tariffInformation?.hasClaimSince2012
      };

      return (
        <div key={partner.id} className="mb-[24px] bg-white-100">
          <div className={classNames(styles.customer, 'mb-[10px] ')}>
            <div className="flex justify-between  py-[24px] px-[16px] ">
              <div className={'flex gap-[8px]  text-navigation-80'}>
                <Icon
                  icon={<UserCircleIcon />}
                  size={'xl'}
                  className={`${styles.icon} mr-[10px]`}
                />
                <div className="flex flex-col gap-[4px]">
                  <Typography className="text-text-60">{t('Versicherte Person')}</Typography>
                  <Typography variant="bodySMBold">
                    {partner.lastname}, {partner.firstname}
                  </Typography>
                </div>
              </div>
              <div className="flex flex-col">
                <Typography variant="bodySMBold" className="flex justify-end">
                  {formatGermanDate(state.model.applicationStart || '')}
                </Typography>
                <Typography className="text-text-60">{t('insuranceStart')}</Typography>
              </div>
            </div>

            <div className="my-s s:my-m">
              <Typography id={`tarifuebersicht`} variant="headlineXLGBold">
                Tarifübersicht der versicherten Person
              </Typography>
            </div>
            <div>{mappedPartner.tariffTable}</div>
            <div
              id={`${partner.id}-details`}
              className="focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <Accordion
                className="bg-[transparent]"
                disableGutters
                elevation={0}
                square
                slotProps={{ transition: { unmountOnExit: true } }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  className={
                    'bg-transparent border border-text-40 border-b-[1px] border-t-0 border-r-0 border-l-0 border-solid px-[16px] py-[0px] mb-[16px] overflow-auto'
                  }
                >
                  <Typography variant="bodyMDBold">{t('details')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="flex justify-between py-[24px]">
                    <InsuredPersonDetail {...mappedPartner} />
                  </div>
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        </div>
      );
    });

  const renderPolicyHolder = () => {
    const policyHolderMapped = {
      // [Personal Data]
      name: optionalValue(policyHolder, formatName) as string,
      firstName: policyHolder.firstname,
      lastName: policyHolder.lastname,
      birthDate: policyHolder.birthDate,
      address: formatAddress(policyHolder.permanentResidence),
      salutation: formatEnum(policyHolder?.salutation, 'SalutationEnum', 'contractsEnums'),
      academicTitle: formatEnum(policyHolder.title, 'TitleEnum', 'contractsEnums'),
      maritalStatus: formatEnum(policyHolder.maritalStatus, 'MaritalStatusEnum', 'contractsEnums'),
      // [Employment Data]
      professionalPosition: formatEnum(
        policyHolder.applicationInformation?.employmentGroup,
        'EmploymentGroupEnum',
        'contractsEnums'
      ),
      currentOccupation: formatEnum(
        policyHolder?.applicationInformation?.profession,
        'ProfessionEnum',
        'contractsEnums'
      ),
      employer: policyHolder.applicationInformation?.employer,
      // [Address]
      isLivingInGermany: policyHolder.livingInGermany,
      street: policyHolder.permanentResidence?.street,
      houseNumber: policyHolder.permanentResidence?.houseNumber,
      postalCode: policyHolder.permanentResidence?.postalCode,
      city: policyHolder.permanentResidence?.city,
      country: findCountryField(
        config.countries || [],
        'code',
        policyHolder.permanentResidence?.country,
        'name'
      ),
      hasForeignResidence: policyHolder.hasLivedInForeignCountry,
      foreignCountry: policyHolder.foreignCountry,
      // [Contact]
      email: policyHolder.permanentResidence?.email,
      phone: policyHolder.permanentResidence?.phone,
      acceptedMarketingStreams: optionalValue(policyHolder, formatMarketingStreams) as string
    };

    return (
      <div className={classNames(styles.customer, 'mb-[24px] bg-white-100')}>
        <Accordion
          className="bg-[transparent]"
          disableGutters
          elevation={0}
          square
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={
              'bg-transparent border border-text-40 border-b-[1px] border-t-0 border-r-0 border-l-0 border-solid px-[16px] py-[0px] mb-[16px] overflow-auto'
            }
          >
            <div className="flex flex-col gap-[8px]">
              <div className={'flex gap-[8px]  text-navigation-80'}>
                <Icon
                  icon={<UserCircleIcon />}
                  size={'xl'}
                  className={`${styles.icon} mr-[10px]`}
                />
                <div className="flex flex-col gap-[4px]">
                  <Typography className="text-text-60">{t('insuredPerson')}</Typography>
                  <Typography variant="bodySMBold">
                    {policyHolder.lastname}, {policyHolder.firstname}
                  </Typography>
                </div>
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails>
            <PolicyHolderDetail key={policyHolder.firstname} {...policyHolderMapped} />
          </AccordionDetails>
        </Accordion>
      </div>
    );
  };

  const renderPaymentContributor = () => {
    const mappedPaymentContributor = {
      // [Personal Data]
      name: optionalValue(paymentContributor, formatName) as string,
      firstName: paymentContributor?.firstname,
      lastName: paymentContributor?.lastname,
      birthDate: paymentContributor?.birthDate,
      address: formatAddress(paymentContributorResidence),
      salutation: formatEnum(paymentContributor?.salutation, 'SalutationEnum', 'contractsEnums'),
      academicTitle: formatEnum(policyHolder.title, 'TitleEnum', 'contractsEnums'),
      // [Address]
      street: paymentContributorResidence?.street,
      houseNumber: paymentContributorResidence?.houseNumber,
      postalCode: paymentContributorResidence?.postalCode,
      city: paymentContributorResidence?.city,
      country: findCountryField(
        config.countries || [],
        'code',
        paymentContributorResidence?.country,
        'name'
      ),

      isPolicyHolder: true,
      iban: paymentContributorAccount?.iban,
      bankName: paymentContributorAccount?.bankName,
      bic: paymentContributorAccount?.bicNumber,
      hasPaymentAuthorization: paymentContributor.bankDetails?.hasPaymentAuthorization
    };

    return (
      <div className={classNames(styles.customer, 'bg-white-100')}>
        <Accordion
          className="bg-[transparent]"
          disableGutters
          elevation={0}
          square
          slotProps={{ transition: { unmountOnExit: true } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={
              'bg-transparent border border-text-40 border-b-[1px] border-t-0 border-r-0 border-l-0 border-solid px-[16px] py-[0px] mb-[16px] overflow-auto'
            }
          >
            <div className={'flex gap-[8px]  text-navigation-80'}>
              <Icon icon={<UserCircleIcon />} size={'xl'} className={`${styles.icon} mr-[10px]`} />
              <div className="flex flex-col gap-[4px]">
                <Typography className="text-text-60">{t('contributor')}</Typography>
                <Typography variant="bodySMBold">
                  {paymentContributor.lastname}, {paymentContributor.firstname}
                </Typography>
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails className="bg-white-100">
            <PaymentContributorDetail
              key={paymentContributor.firstname}
              {...mappedPaymentContributor}
            />
          </AccordionDetails>
        </Accordion>
      </div>
    );
  };

  return (
    <WizardSlot.Main>
      <DownloadOfferDialog
        isOpen={isDialogOpen}
        hasConsent={hasConsent}
        onDownload={downloadFile}
        onDownloadDisclaimerDocument={() =>
          downloadDisclaimerDocument(state.model.applicationStart)
        }
        onConsentClick={handleConsentClick}
        onClose={() => {
          setIsDialogOpen(false);
        }}
      />
      <div className="mx-[16px] m:mx-[80px] pb-[32px]">
        <div className="my-s s:my-m">
          <Typography
            component={'h1'}
            variant="headlineXXXLGBold"
            className="text-navigation-80 text-center s:text-left m-[0px] py-m"
          >
            {t('summaryHeader')}
          </Typography>
        </div>

        <Form state={state} dispatch={dispatch}>
          <div className="space-y-[32px]">
            <Typography variant="headlineXXLGBold" className="text-navigation-80">
              Versicherte Person(en)
            </Typography>
            {renderInsuredPersons()}
            <Typography variant="headlineXXLGBold" className="text-navigation-80">
              Versicherungsnehmer:in
            </Typography>
            {renderPolicyHolder()}
            <Typography variant="headlineXXLGBold" className="text-navigation-80">
              Beitragszahler:in
            </Typography>
            {renderPaymentContributor()}
          </div>

          <div className="bg-white-100">
            <div className="my-s s:my-m">
              <Typography
                component={'h2'}
                id={`datenschutz`}
                variant="headlineXXLGBold"
                className="text-navigation-80"
              >
                Einwilligungen und Vertragsinformationen
              </Typography>
            </div>
            <div className="mb-[16px]">
              <Typography variant="headlineXLGBold">{t('dataProtectionHeader')}</Typography>
            </div>

            <div className="mb-[24px]">
              <DisclaimerCheckbox
                id={EVENT_IDS.DATA_PROTECTION_CHECKBOX}
                className="w-full"
                componentKey={partnerField(
                  holderId,
                  'applicationInformation.acceptsDataProtection'
                )}
                description={
                  <Typography variant="bodyMDRegular">
                    <Trans
                      i18nKey={'summaryPage:dataProtectionDescription'}
                      components={[
                        <a
                          key="sendMail"
                          onClick={sendMail}
                          className="m-px text-logo-500 cursor-pointer inline underline"
                        />
                      ]}
                    />
                  </Typography>
                }
              />
            </div>

            <div className="mb-[16px]">
              <Typography variant="headlineXLGBold">{t('consentToAdvice')}</Typography>
            </div>

            <div className="mb-[24px]">
              <DisclaimerCheckbox
                id={EVENT_IDS.WAIVE_ADVICE_CHECKBOX}
                className="w-full"
                componentKey={partnerField(holderId, 'applicationInformation.acceptsToWaiveAdvice')}
                description={
                  <Typography variant="bodyMDRegular">{t('consentToAdviceDescription')}</Typography>
                }
              />
            </div>

            <div className="mb-[16px]">
              <Typography variant="headlineXLGBold">{t('contactInformation')}</Typography>
            </div>

            <div className="mb-[6px]">
              <Typography className={'text-lightText'}>{t('documentInformation')}</Typography>
            </div>

            <Typography variant="bodySMRegular">{t('contractInformationListHeader')}</Typography>
            <Typography variant="bodySMRegular">
              <ul className="mt-[8px] mb-[24px]">
                <li>{t('contractListFirst')}</li>
                <li>{t('contractListSecond')}</li>
                <li>{t('contractListThird')}</li>
                <li>{t('contractListFourth')}</li>
                <li>{t('contractListFifth')}</li>
              </ul>
            </Typography>

            <div className="mb-[24px]">
              <AccompaniedDocumentsDownload onClick={() => setIsDialogOpen(true)} />
            </div>

            <div className="mb-[24px]">
              <DisclaimerCheckbox
                className="flex items-center justify-start w-full"
                id={EVENT_IDS.CONTRACT_INFORMATION_CHECKBOX}
                componentKey={partnerField(
                  holderId,
                  'applicationInformation.acceptsContractInformation'
                )}
                description={
                  <Typography variant="bodyMDRegular">
                    {t('contractualInformationConfirmation')}
                  </Typography>
                }
              />
            </div>

            <div className="mb-[24px]">
              <Typography variant="bodySMRegular">{t('contractualInformationInfo')}</Typography>
            </div>

            <div className="mb-[24px]">
              <DisclaimerCheckbox
                id={EVENT_IDS.DATA_PROCESSING_CHECKBOX}
                componentKey={partnerField(
                  holderId,
                  'applicationInformation.acceptsDataCollection'
                )}
                description={
                  <Typography variant="bodyMDRegular">
                    {t('collectionInformationConsent')}
                  </Typography>
                }
              />
            </div>
            <div className="mb-[24px]">
              <DisclaimerCheckbox
                id={EVENT_IDS.REVOCATION_CONDITIONS_CHECKBOX}
                componentKey={partnerField(
                  holderId,
                  'applicationInformation.acceptsRevocationConditions'
                )}
                description={
                  <Typography variant="bodyMDRegular">{t('revocationConditions')}</Typography>
                }
              />
            </div>

            <div className="mb-[16px]">
              <Typography variant="headlineXLGBold">{t('dataUseForAdds')}</Typography>
            </div>

            <MarketingConsentForm holderId={holderId} />
          </div>
        </Form>
      </div>
      <NavigationSection />
    </WizardSlot.Main>
  );
};
